import { AmplifyConfig } from '../types/amplifyConfig.types';

interface AwsConfig {
    [key: string]: string;
  }
  
  interface ConfigResponse {
    type: 'default' | 'legacy';
    config: AwsConfig;
  }
  
export class AwsConfigFetcher {
    private static instance: AwsConfigFetcher;
    private _config: AwsConfig | null = null;
    private _endPointReady = false; // temporary flag for fallback handling
    private _mode: 'default' | 'legacy' = 'legacy'; // default to legacy as fallback
    private _amplifyConfig: AmplifyConfig | null = null;
  
    get endPointReady(): boolean {
      return this._endPointReady;
    }
  
    get config(): AwsConfig {
      return this._config ? { ...this._config } : {};
    }
  
    get mode(): 'default' | 'legacy' {
      return this._mode;
    }
  
    set mode(mode: 'default' | 'legacy') {
      if (!['default', 'legacy'].includes(mode)) {
        throw new Error("Mode can only be 'default' or 'legacy'");
      }
      this._mode = mode;
    }
  
    private constructor() {}
  
    /**
     * Fetches AWS config from backend endpoint or falls back to local config
     */
    async getAwsConfig(): Promise<AwsConfig> {
      // Return cached config if already fetched
      if (this._config) {
        return this.config;
      }
  
      try {
        const configUrl = `${import.meta.env.VITE_BASE_API_URL}/config/cognito`;
        
        const response = await fetch(configUrl);
        console.log("in getAwsConfig()");
        
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
  
        const data: ConfigResponse = await response.json();
        
        // Validate response structure
        if (!data.type || !data.config) {
          throw new Error('Invalid config response structure');
        }
  
        // Set mode based on response type
        switch (data.type) {
          case 'default':
            this.mode = 'default';
            break;
          case 'legacy':
            this.mode = 'legacy';
            break;
          default:
            throw new Error(`Invalid type: ${data.type}. Expected 'default' or 'legacy'`);
        }
  
        this._config = data.config;
        this._endPointReady = true;
        
        console.log(`AWS Config loaded in ${this.mode} mode`);
        return this.config;
  
      } catch (error) {
        console.warn('Failed to fetch AWS config from endpoint:', error);
        
        if (this.endPointReady) {
          // If we expected the endpoint to work but it failed, throw the error
          throw new Error(`AWS Config fetch failed: ${error}`);
        } else {
          // Fallback to legacy mode with local/default config
          console.log('Falling back to legacy mode with local config');
          this.mode = 'legacy';
          
          const fallbackConfig: AwsConfig = {
            aws_cognito_region: import.meta.env.VITE_AWS_REGION || 'eu-west-1',
            aws_user_pools_id: import.meta.env.VITE_USER_POOL_ID,
            aws_user_pools_web_client_id: import.meta.env.VITE_USER_POOL_WEB_CLIENT_ID,
            aws_cognito_identity_pool_id: import.meta.env.VITE_IDENTITY_POOL_ID,
            aws_mandatory_sign_in: 'enable',
            aws_user_files_s3_bucket: import.meta.env.VITE_AWS_S3_BUCKET,
            aws_user_files_s3_bucket_region: 'eu-west-1',
          };
          
          this._config = fallbackConfig;
          return this.config;
        }
      }
    }
  
    /**
     * Initialize AWS Amplify with complete configuration (Auth + Storage)
     */
    async initializeAmplify(): Promise<void> {
        if (!this._config) {
          throw new Error('Config not loaded. Call getAwsConfig() first.');
        }
      
        const { Amplify } = await import('aws-amplify');
      
        const amplifyConfig: AmplifyConfig = {
          Auth: {
            region: this._config.aws_cognito_region,
            userPoolId: this._config.aws_user_pools_id,
            userPoolWebClientId: this._config.aws_user_pools_web_client_id,
            identityPoolId: this._config.aws_cognito_identity_pool_id,
            identityPoolRegion: this._config.aws_cognito_region,
            mandatorySignIn: this._config.aws_mandatory_sign_in === 'enable',
            signUpVerificationMethod: 'code',
          },
          Storage: {
            AWSS3: {
              bucket: this._config.aws_user_files_s3_bucket,
              region: this._config.aws_user_files_s3_bucket_region,
            },
          },
        };
        this._amplifyConfig = amplifyConfig;
        Amplify.configure(amplifyConfig);
        console.log('Amplify configured with', this.mode, 'mode');
      }
  
    /**
     * Reset the instance
     */
    reset(): void {
      this._config = null;
      this._endPointReady = false;
      this._mode = 'legacy';
    }
  
    public static getInstance(): AwsConfigFetcher {
      if (!AwsConfigFetcher.instance) {
        AwsConfigFetcher.instance = new AwsConfigFetcher();
      }
      return AwsConfigFetcher.instance;
    }

    // Safely returns a shallow copy of the Amplify config or null if not initialized
    get amplifyConfig(): AmplifyConfig | null {
        return this._amplifyConfig ? { ...this._amplifyConfig } : null;
      }
  }
  
  // Export singleton instance for easy access
  export default AwsConfigFetcher.getInstance();